// Swiping a drawer down by its top (the grabber and the row under it) on a
// phone. Shared by the drawers that rise from the bottom (bottom-drawer.tsx,
// turn-drawer.tsx): a grabber that does not move with the finger reads as
// broken (2026-10-18, an iPhone: the post box's conditions drawer showed a
// grabber that could not be pulled).
//
// Promise:
// - The area that takes the handlers has `touch-action: none` (the caller's
//   class): the vertical move is ours, not the browser's scroll (iOS cancels
//   the pointer once it pans).
// - A move shorter than 8 px is a press, not a swipe.
// - While swiping, the drawer follows the finger downward (never above where
//   it rests); released past `closeRatio` of its height or on a quick flick
//   down, it closes; else it goes back.
// - The click that ends a swipe is dropped (it is not a press on what lies
//   under the finger).
import { type PointerEvent, useRef, useState } from "react";

/** A move shorter than this is a press, not a swipe. */
const DRAG_MIN_PX = 8;
/** The speed of a swipe is read over its last this long. */
const FLICK_WINDOW_MS = 100;
/** Released faster than this downward, it closes. */
const FLICK_SPEED_PX_PER_MS = 0.5;

export function useSwipeDown(
  onClose: () => void,
  box: () => HTMLElement | null,
  /** Pulled down slowly, it closes past this share of its height. */
  closeRatio: number,
) {
  const start = useRef<{ id: number; y: number; moved: boolean } | null>(null);
  const samples = useRef<{ y: number; t: number }[]>([]);
  const [dragY, setDragY] = useState(0);
  const end = (e: PointerEvent<HTMLElement>, cancel: boolean) => {
    const s = start.current;
    if (!s || s.id !== e.pointerId) return;
    start.current = null;
    setDragY(0);
    if (!s.moved) return;
    const swallow = (ev: Event) => {
      ev.stopPropagation();
      ev.preventDefault();
    };
    window.addEventListener("click", swallow, { capture: true, once: true });
    window.setTimeout(
      () => window.removeEventListener("click", swallow, { capture: true }),
      0,
    );
    if (cancel) return;
    const distance = Math.max(0, e.clientY - s.y);
    const now = performance.now();
    const from = samples.current.find((p) => now - p.t <= FLICK_WINDOW_MS);
    const speed =
      from && now > from.t ? (e.clientY - from.y) / (now - from.t) : 0;
    const height = box()?.getBoundingClientRect().height ?? 0;
    if (distance >= height * closeRatio || speed >= FLICK_SPEED_PX_PER_MS)
      onClose();
  };
  return {
    dragY,
    handlers: {
      onPointerDown: (e: PointerEvent<HTMLElement>) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        start.current = { id: e.pointerId, y: e.clientY, moved: false };
        samples.current = [{ y: e.clientY, t: performance.now() }];
      },
      onPointerMove: (e: PointerEvent<HTMLElement>) => {
        const s = start.current;
        if (!s || s.id !== e.pointerId) return;
        const dy = e.clientY - s.y;
        if (!s.moved) {
          if (dy < DRAG_MIN_PX) return;
          s.moved = true;
          e.currentTarget.setPointerCapture?.(e.pointerId);
        }
        setDragY(Math.max(0, dy));
        const now = performance.now();
        samples.current = [
          ...samples.current.filter((p) => now - p.t <= FLICK_WINDOW_MS),
          { y: e.clientY, t: now },
        ];
      },
      onPointerUp: (e: PointerEvent<HTMLElement>) => end(e, false),
      onPointerCancel: (e: PointerEvent<HTMLElement>) => end(e, true),
    },
  };
}
